import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { FaArrowLeft, FaEdit, FaFileExcel, FaFilePdf, FaPaperPlane, FaReply, FaSearch } from "react-icons/fa";
import {
  MODULE_PAGE_SHELL_STYLE,
  TASK_PAGE_SHELL_CLASS,
} from "../../TaskManagement/workspaceConstants";
import {
  fetchInquiryBatch,
  updateInquiryBatch,
  deleteInquiryBatchItem,
} from "./inquiryBatchApi";
import { mapWithConcurrency } from "../../../utilis/mapWithConcurrency";
import {
  formatBatchDateTime,
  getBatchStatusMeta,
  areSameProviderBatchItems,
  getBatchItemProviderKey,
  getConsecutiveProviderItems,
  BATCH_ITEM_LINE_FILTERS,
  filterBatchItemsByStatus,
} from "./inquiryBatchUtils";
import {
  batchItemToApplicationShape,
  canSendBatchItemToProvider,
} from "./inquiryApplicationUtils";
import InquiryBatchReplyDock from "./InquiryBatchReplyDock";
import InquiryBatchSendProviderModal, {
  InquiryBatchProviderEmailHistory,
} from "./InquiryBatchSendProviderModal";
import InquiryBatchViewReplyModal from "./InquiryBatchViewReplyModal";
import InquiryFillModal from "./InquiryFillModal";
import ClientDocumentsModal from "./ClientDocumentsModal";
import CommissionsHeader from "./CommissionsHeader";
import CommissionsReportTabs from "./CommissionsReportTabs";
import InquiryBatchStudentsTable from "./InquiryBatchStudentsTable";
import {
  downloadInquiryBatchExcel,
  downloadInquiryBatchPdf,
  filterBatchItemsByProvider,
  sortBatchItemsByProvider,
} from "./inquiryBatchExportUtils";
import {
  canViewCommissionModule,
  canViewCommissionableStudentsTab,
} from "../../../utilis/invoicePermissionHelpers";
import "../../../styles/tailwind.css";
import "./commissions-responsive.css";

const inputClass =
  "h-9 w-full rounded border border-slate-200 bg-white px-2.5 text-[13px] font-medium text-slate-800 outline-none transition focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20";

function StatusBadge({ meta }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${meta.className}`}
    >
      {meta.label}
    </span>
  );
}

function InquiryBatchDetailPage() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [batch, setBatch] = useState(null);
  const [error, setError] = useState("");
  const [editingMeta, setEditingMeta] = useState(false);
  const [batchName, setBatchName] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [replyDockOpen, setReplyDockOpen] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [viewReplyItem, setViewReplyItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [documentsModal, setDocumentsModal] = useState(null);
  const [providerSearch, setProviderSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    setError("");
    try {
      const data = await fetchInquiryBatch(batchId);
      setBatch(data);
      setBatchName(data?.batch_name || data?.subject || "");
      setNotes(data?.notes || "");
    } catch (err) {
      setError(err?.message || "Failed to load batch.");
      setBatch(null);
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    load();
  }, [load]);

  const items = useMemo(
    () => (Array.isArray(batch?.items) ? batch.items : []),
    [batch],
  );

  const sortedItems = useMemo(
    () => sortBatchItemsByProvider(items),
    [items],
  );

  const filteredItems = useMemo(() => {
    const byProvider = filterBatchItemsByProvider(sortedItems, providerSearch);
    return filterBatchItemsByStatus(byProvider, statusFilter);
  }, [sortedItems, providerSearch, statusFilter]);

  const selectedItems = useMemo(
    () => items.filter((item) => selectedIds.has(Number(item.id))),
    [items, selectedIds],
  );

  const sendableItems = useMemo(
    () =>
      selectedItems.filter((item) => canSendBatchItemToProvider(item, batch)),
    [selectedItems, batch],
  );

  const sendableSameProvider = useMemo(
    () => areSameProviderBatchItems(sendableItems),
    [sendableItems],
  );

  const selectableItems = useMemo(
    () =>
      filteredItems.filter(
        (item) =>
          !["moved_to_commission", "cancelled"].includes(item.item_status),
      ),
    [filteredItems],
  );

  const allSelectableChecked =
    selectableItems.length > 0 &&
    selectableItems.every((item) => selectedIds.has(Number(item.id)));

  const exportCount =
    selectedIds.size > 0 ? selectedIds.size : items.length;

  const handleExportExcel = () => {
    try {
      downloadInquiryBatchExcel(items, batch, selectedIds);
    } catch (err) {
      Swal.fire({
        icon: "warning",
        title: "Nothing to export",
        text: err?.message || "No student records available.",
        confirmButtonColor: "#007BFF",
      });
    }
  };

  const handleExportPdf = () => {
    try {
      downloadInquiryBatchPdf(items, batch, selectedIds);
    } catch (err) {
      Swal.fire({
        icon: "warning",
        title: "Nothing to export",
        text: err?.message || "No student records available.",
        confirmButtonColor: "#007BFF",
      });
    }
  };

  const toggleItem = (itemId) => {
    const id = Number(itemId);
    const item = items.find((row) => Number(row.id) === id);
    if (!item) return;

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        return next;
      }

      if (next.size > 0) {
        const firstSelected = items.find((row) => next.has(Number(row.id)));
        if (
          firstSelected &&
          getBatchItemProviderKey(firstSelected) !== getBatchItemProviderKey(item)
        ) {
          return new Set([id]);
        }
      }

      next.add(id);
      return next;
    });
  };

  const selectProviderGroup = (providerName, startIndex, allGroupSelected = false) => {
    if (allGroupSelected) {
      setSelectedIds(new Set());
      return;
    }

    const groupItems = getConsecutiveProviderItems(
      filteredItems,
      providerName,
      startIndex,
    );
    const ids = groupItems
      .filter(
        (item) =>
          !["moved_to_commission", "cancelled"].includes(item.item_status),
      )
      .map((item) => Number(item.id));

    setSelectedIds(new Set(ids));
  };

  const handleOpenSendModal = async () => {
    if (sendableItems.length === 0) {
      if (selectedItems.length > 0) {
        await Swal.fire({
          icon: "warning",
          title: "Cannot send",
          text: "Only open or sent students with complete inquiry details can be emailed. Replied, moved to commission, or incomplete rows are excluded.",
          confirmButtonColor: "#007BFF",
        });
      }
      return;
    }

    if (!sendableSameProvider) {
      await Swal.fire({
        icon: "warning",
        title: "One provider only",
        text: "Select open or sent, complete students from a single provider before sending email.",
        confirmButtonColor: "#007BFF",
      });
      return;
    }

    setSendModalOpen(true);
  };

  const toggleAll = () => {
    if (allSelectableChecked) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(selectableItems.map((item) => Number(item.id))));
  };

  const openReplyForItems = (targetItems = []) => {
    const ids = targetItems
      .filter((item) => item?.id != null)
      .map((item) => Number(item.id));
    if (!ids.length) return;
    setSelectedIds(new Set(ids));
    setReplyDockOpen(true);
  };

  const openSendForItems = async (targetItems = []) => {
    const sendable = targetItems.filter((item) =>
      canSendBatchItemToProvider(item, batch),
    );
    if (!sendable.length) {
      await Swal.fire({
        icon: "warning",
        title: "Cannot send",
        text: "Only open or sent students with complete inquiry details can be emailed to the provider.",
        confirmButtonColor: "#007BFF",
      });
      return;
    }

    if (!areSameProviderBatchItems(sendable)) {
      Swal.fire({
        icon: "warning",
        title: "One provider only",
        text: "Send email to one provider at a time.",
        confirmButtonColor: "#007BFF",
      });
      return;
    }

    const ids = sendable.map((item) => Number(item.id));
    setSelectedIds(new Set(ids));
    setSendModalOpen(true);
  };

  const runAction = async (fn, successTitle) => {
    setBusy(true);
    try {
      const result = await fn();
      if (result?.batch) setBatch(result.batch);
      else if (result?.id) setBatch(result);
      else await load();
      if (successTitle) {
        await Swal.fire({
          icon: "success",
          title: successTitle,
          timer: 1400,
          showConfirmButton: false,
        });
      }
      return true;
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "Action failed",
        text: err?.message || "Something went wrong.",
        confirmButtonColor: "#007BFF",
      });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleSaveMeta = async () => {
    const ok = await runAction(
      () =>
        updateInquiryBatch(batchId, {
          batch_name: String(batchName || "").trim(),
          notes,
        }),
      "Batch updated",
    );
    if (ok) setEditingMeta(false);
  };

  const batchStatus = getBatchStatusMeta(batch?.status);
  const counts = batch?.counts || {};

  const handleDeleteItem = async (item) => {
    const confirmed = await Swal.fire({
      title: "Remove student?",
      text: `Are you sure you want to remove ${
        item.student_name || "this student"
      } from this batch?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remove",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!confirmed.isConfirmed) return;

    setBusy(true);

    try {
      await deleteInquiryBatchItem(batchId, item.id);

      await Swal.fire({
        icon: "success",
        title: "Student removed",
        text: "The student was removed from this batch successfully.",
        timer: 1400,
        showConfirmButton: false,
      });

      await load();
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(Number(item.id));
        return next;
      });
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "Failed",
        text: err?.message || "Failed to remove student from batch.",
        confirmButtonColor: "#007BFF",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteSelectedItems = async () => {
    const ids = Array.from(selectedIds);

    if (!ids.length) return;

    const confirmed = await Swal.fire({
      title: "Remove selected students?",
      text: `Are you sure you want to remove ${ids.length} selected student${
        ids.length > 1 ? "s" : ""
      } from this batch?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remove",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!confirmed.isConfirmed) return;

    setBusy(true);

    try {
      await mapWithConcurrency(ids, (itemId) =>
        deleteInquiryBatchItem(batchId, itemId),
      );

      setSelectedIds(new Set());

      await load();

      await Swal.fire({
        icon: "success",
        title: "Students removed",
        text: `${ids.length} student${
          ids.length > 1 ? "s were" : " was"
        } removed from this batch successfully.`,
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "Failed",
        text:
          err?.message ||
          "Some selected students could not be removed from the batch.",
        confirmButtonColor: "#007BFF",
      });
    } finally {
      setBusy(false);
    }
  };

  if (!canViewCommissionModule()) {
    return (
      <div className="tailwind-container p-4">
        <p className="text-muted mb-0">
          You do not have permission to view the commission module.
        </p>
      </div>
    );
  }

  const canCommissionStudents = canViewCommissionableStudentsTab();

  return (
    <div className="tailwind-container">
      <div
        className={`${TASK_PAGE_SHELL_CLASS} cms-commissions-shell`}
        style={MODULE_PAGE_SHELL_STYLE}
        aria-busy={loading || busy}
      >
        <CommissionsHeader
          onExport={() => {}}
          loading={loading}
          showInquiryBatches={false}
        />

        <div className="mt-3">
          <CommissionsReportTabs
            value="inquiry_batches"
            onChange={(report) => {
              if (report === "inquiry_batches") {
                navigate("/commissions-invoices?report=inquiry_batches");
                return;
              }
              if (report === "commission" && !canCommissionStudents) {
                navigate("/commissions-invoices?report=general");
                return;
              }
              navigate(
                report === "commission"
                  ? "/commissions-invoices?report=commission"
                  : "/commissions-invoices?report=general",
              );
            }}
            visible
            showCommissionStudents={canCommissionStudents}
          />
        </div>

        <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <button
              type="button"
              onClick={() =>
                navigate("/commissions-invoices?report=inquiry_batches")
              }
              className="mb-1.5 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#007BFF] hover:underline"
              style={{ border: "none", background: "transparent", padding: 0 }}
            >
              <FaArrowLeft size={10} />
              Back to Inquiry Batches
            </button>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-bold leading-tight text-[#35314d] sm:text-xl">
                {batch?.batch_no || (loading ? "Loading…" : "Batch")}
              </h2>
              {batch ? <StatusBadge meta={batchStatus} /> : null}
            </div>
            <p className="mt-0.5 text-[13px] text-gray-500">
              {batch?.batch_name || batch?.subject || "Untitled batch"}
              {" · "}
              Created {formatBatchDateTime(batch?.created_at)}
              {batch?.updated_at
                ? ` · Updated ${formatBatchDateTime(batch.updated_at)}`
                : ""}
              {batch?.sent_at
                ? ` · Sent ${formatBatchDateTime(batch.sent_at)}`
                : ""}
            </p>
          </div>
        </div>

        {error ? (
          <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] font-medium text-rose-700">
            {error}
          </p>
        ) : null}

        {batch ? (
          <>
            <div className="mt-3 flex flex-wrap gap-2">
              {[
                ["Total", counts.total ?? items.length],
                [
                  "Open",
                  counts.pending ??
                    counts.open ??
                    batch?.open_items_count ??
                    0,
                ],
                [
                  "Sent",
                  counts.sent ?? batch?.sent_items_count ?? 0,
                ],
                [
                  "Replied",
                  counts.replied ??
                    batch?.replied_items_count ??
                    0,
                ],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="min-w-[96px] rounded-md border border-slate-200 bg-white px-2.5 py-2 shadow-sm"
                >
                  <p className="mb-0 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                    {label}
                  </p>
                  <p className="mb-0 text-[16px] font-semibold tabular-nums text-slate-900">
                    {value}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-3 rounded-md border border-gray-200 bg-white p-3 shadow-sm">
              <div className="mb-2 flex items-center justify-between gap-2">
                <h2 className="mb-0 text-[13px] font-semibold text-slate-800">
                  Batch notes
                </h2>
                {!editingMeta ? (
                  <button
                    type="button"
                    onClick={() => setEditingMeta(true)}
                    disabled={busy || batch.status === "cancelled"}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 px-2.5 text-[12px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                    style={{ boxShadow: "none" }}
                  >
                    <FaEdit size={11} />
                    Edit
                  </button>
                ) : null}
              </div>

              {editingMeta ? (
                <div className="space-y-2.5">
                  <div>
                    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Batch name
                    </label>
                    <input
                      type="text"
                      value={batchName}
                      onChange={(e) => setBatchName(e.target.value)}
                      className={inputClass}
                      disabled={busy}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Internal notes
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      className="w-full rounded border border-slate-200 px-2.5 py-2 text-[13px] outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20"
                      disabled={busy}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingMeta(false);
                        setBatchName(batch.batch_name || batch.subject || "");
                        setNotes(batch.notes || "");
                      }}
                      className="h-8 rounded-md border border-slate-200 px-2.5 text-[12px] font-semibold text-slate-600"
                      style={{ boxShadow: "none" }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveMeta}
                      disabled={busy}
                      className="h-8 rounded-md bg-[#007BFF] px-2.5 text-[12px] font-semibold text-white disabled:opacity-60"
                      style={{ border: "none", boxShadow: "none" }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <p className="mb-0 whitespace-pre-wrap text-[13px] text-slate-700">
                  {batch.notes || "No internal notes"}
                </p>
              )}
            </div>

            <div className="mt-3 overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm">
              <div className="cms-commissions-inquiry-toolbar flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <h2 className="mb-0 text-[13px] font-semibold text-slate-800">
                    Students
                  </h2>
                  <p className="mb-0 text-[12px] text-slate-500">
                    Send open students to the provider, resend follow-up emails for
                    sent students, then record a reply. Replied or commission
                    students cannot be emailed again.
                  </p>
                </div>

                <div className="cms-commissions-batch-toolbar">
                  <div className="cms-commissions-batch-toolbar__search relative shrink-0">
                    <FaSearch
                      size={11}
                      className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      type="search"
                      value={providerSearch}
                      onChange={(event) => setProviderSearch(event.target.value)}
                      placeholder="Search provider name"
                      className="h-9 w-full rounded-md border border-slate-200 bg-white pl-8 pr-3 text-[13px] text-slate-800 outline-none transition focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20"
                    />
                  </div>

                  <div className="cms-commissions-batch-toolbar__actions">
                  <select
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    aria-label="Filter students by status"
                    className="h-9 shrink-0 rounded-md border border-slate-200 bg-white px-2.5 text-[13px] font-medium text-slate-800 outline-none transition focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20"
                  >
                    {BATCH_ITEM_LINE_FILTERS.map((option) => (
                      <option key={option.id || "all"} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    disabled={busy || exportCount === 0}
                    onClick={handleExportExcel}
                    className="inline-flex h-9 items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 text-[13px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-45"
                    style={{ boxShadow: "none" }}
                  >
                    <FaFileExcel size={12} />
                    Excel
                    {selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
                  </button>

                  <button
                    type="button"
                    disabled={busy || exportCount === 0}
                    onClick={handleExportPdf}
                    className="inline-flex h-9 items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-3 text-[13px] font-semibold text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-45"
                    style={{ boxShadow: "none" }}
                  >
                    <FaFilePdf size={12} />
                    PDF
                    {selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
                  </button>

                  <button
                    type="button"
                    disabled={busy || sendableItems.length === 0 || !sendableSameProvider}
                    onClick={handleOpenSendModal}
                    title={
                      sendableItems.length > 0 && !sendableSameProvider
                        ? "Select open or sent, complete students from one provider only"
                        : sendableItems.length === 0 && selectedItems.length > 0
                          ? "Selected students cannot be sent (missing details, replied, or moved to commission)"
                          : "Send or resend email to provider"
                    }
                    className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#007BFF] px-3 text-[13px] font-semibold text-white hover:bg-[#0069D9] disabled:cursor-not-allowed disabled:opacity-45"
                    style={{ border: "none", boxShadow: "none" }}
                  >
                    <FaPaperPlane size={12} />
                    Send / resend
                    {sendableItems.length > 0
                      ? ` (${sendableItems.length})`
                      : ""}
                  </button>

                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setReplyDockOpen(true)}
                    title="Record provider reply"
                    className="inline-flex h-9 items-center gap-1.5 rounded-md bg-violet-600 px-3 text-[13px] font-semibold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-45"
                    style={{ border: "none", boxShadow: "none" }}
                  >
                    <FaReply size={12} />
                    Reply
                    {selectedItems.length > 0
                      ? ` (${selectedItems.length})`
                      : ""}
                  </button>

                  {selectedItems.length > 0 ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={handleDeleteSelectedItems}
                      className="inline-flex h-9 items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-3 text-[13px] font-semibold text-red-600 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                      style={{ boxShadow: "none" }}
                    >
                      Delete selected ({selectedItems.length})
                    </button>
                  ) : null}
                  </div>
                </div>
              </div>

              {providerSearch.trim() || statusFilter ? (
                <p className="border-b border-slate-100 bg-slate-50/50 px-3 py-2 text-[12px] text-slate-500">
                  Showing {filteredItems.length} of {items.length} students
                  {statusFilter ? (
                    <>
                      {" "}
                      with status{" "}
                      <span className="font-medium text-slate-700">
                        {
                          BATCH_ITEM_LINE_FILTERS.find(
                            (option) => option.id === statusFilter,
                          )?.label
                        }
                      </span>
                    </>
                  ) : null}
                  {providerSearch.trim() ? (
                    <>
                      {statusFilter ? " and" : " for"} provider &quot;
                      {providerSearch.trim()}&quot;
                    </>
                  ) : null}
                </p>
              ) : null}

              <InquiryBatchStudentsTable
                batch={batch}
                filteredItems={filteredItems}
                loading={loading}
                busy={busy}
                selectedIds={selectedIds}
                allSelectableChecked={allSelectableChecked}
                selectableItems={selectableItems}
                providerSearch={providerSearch}
                onToggleAll={toggleAll}
                onToggleItem={toggleItem}
                onSelectProviderGroup={selectProviderGroup}
                onEditItem={setEditItem}
                onOpenDocuments={setDocumentsModal}
                onReplyItem={(item) => openReplyForItems([item])}
                onSendItem={(item) => openSendForItems([item])}
                onViewReplyItem={setViewReplyItem}
                onDeleteItem={handleDeleteItem}
              />
            </div>

            <InquiryBatchProviderEmailHistory emails={batch.provider_emails} />
          </>
        ) : null}
      </div>

      <InquiryBatchSendProviderModal
        open={sendModalOpen && sendableItems.length > 0}
        batchId={batchId}
        batch={batch}
        items={sendableItems}
        onClose={() => setSendModalOpen(false)}
        onSent={async (result) => {
          setSendModalOpen(false);
          if (result?.batch) setBatch(result.batch);
          else await load();
          setSelectedIds(new Set());
          const isResend = Boolean(result?.is_resend);
          await Swal.fire({
            icon: "success",
            title: isResend ? "Email resent" : "Email sent",
            text: isResend
              ? "Follow-up provider email sent with student Excel attached."
              : "Provider email sent with student Excel attached.",
            timer: 1800,
            showConfirmButton: false,
          });
        }}
      />

      <InquiryBatchReplyDock
        open={replyDockOpen && selectedItems.length > 0}
        batchId={batchId}
        items={selectedItems}
        onClose={() => setReplyDockOpen(false)}
        onSaved={async (result) => {
          setReplyDockOpen(false);
          if (result?.batch) setBatch(result.batch);
          else await load();
          setSelectedIds(new Set());
          await Swal.fire({
            icon: "success",
            title: "Reply saved",
            timer: 1400,
            showConfirmButton: false,
          });
        }}
      />

      <InquiryBatchViewReplyModal
        open={Boolean(viewReplyItem)}
        item={viewReplyItem}
        onClose={() => setViewReplyItem(null)}
      />

      <InquiryFillModal
        open={Boolean(editItem)}
        batchId={batchId}
        application={
          editItem ? batchItemToApplicationShape(editItem, batch) : null
        }
        validationMode="batch"
        title="Edit inquiry details"
        mode="fill"
        onClose={() => setEditItem(null)}
        onSaved={async () => {
          setEditItem(null);
          await load();
        }}
      />

      <ClientDocumentsModal
        open={Boolean(documentsModal)}
        clientName={documentsModal?.clientName}
        documents={documentsModal?.documents}
        onClose={() => setDocumentsModal(null)}
      />
    </div>
  );
}

export default InquiryBatchDetailPage;
